import type { ReactElement } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { rotuloMes } from './grafico-financeiro'
import type { PontoFinanceiro } from './types'

import { EmptyState } from '@/components/ui/empty-state'
import { moedaCompacta, percentual } from '@/lib/formato'

export function formatarPct(valor: number): string {
  return percentual(valor, 0)
}

export function formatarValorInad(valor: unknown, nome: unknown): [string, string] {
  const n = Number(valor)
  return [nome === 'Recuperação' ? percentual(n, 1) : moedaCompacta(n), String(nome)]
}

export interface GraficoInadimplenciaProps {
  pontos: PontoFinanceiro[]
}

/** Inadimplência (R$, eixo esquerdo) × taxa de recuperação (%, eixo direito) por mês. */
export function GraficoInadimplencia({ pontos }: GraficoInadimplenciaProps): ReactElement {
  if (pontos.length === 0) {
    return <EmptyState size="sm" title="Sem histórico no período." />
  }

  const dados = pontos.map((p) => ({ ...p, rotulo: rotuloMes(p.mes) }))

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={dados} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="rotulo"
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            yAxisId="rs"
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={moedaCompacta}
          />
          <YAxis
            yAxisId="pct"
            orientation="right"
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={44}
            domain={[0, 1]}
            tickFormatter={formatarPct}
          />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              color: 'hsl(var(--popover-foreground))',
            }}
            formatter={formatarValorInad}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line
            yAxisId="rs"
            dataKey="inadimplencia"
            name="Inadimplência"
            type="monotone"
            stroke="#e76f51"
            strokeWidth={2}
            dot={false}
          />
          <Line
            yAxisId="pct"
            dataKey="recuperacao"
            name="Recuperação"
            type="monotone"
            stroke="#5fe3c0"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
